// Will contain types that is used internally in the app

import type { Channels, RChunk, RTask } from './api/conversations'
import type { HandoffCaller } from './api/handoff-context'
import type { RWhatsappTemplate } from './api/settings'
import type {
  ChunkOrigin,
  FileMessageContent,
  RAiMessageChunk,
  RChatButtonChunk,
  RChatMenuButtonChunk,
  RChatMultiProductChunk,
  RChatMultiProductReplyChunk,
  RFlowExecutionChunkMeta,
  RSignalChunk,
} from './chunk.external'

// And is extracted from the external types - ./chunk.external.ts
export enum ChunkType {
  chatMessage = 'message',
  chatButton = 'chat_button',
  file = 'file',
  signal = 'signal',
  text = 'text',
  flowExecution = 'flow_execution',
  notes = 'notes',
  task = 'task',
  disposition = 'disposition',
  unknown = 'unknown',
  aiMessage = 'aichat',
  chatMultiProduct = 'chat_multi_product',
  multiProductReply = 'multi_product_reply',
  chatMenuButton = 'chat_menu_button',
  template = 'template',
  button = 'button', // This is the chunk type for whatsapp reply message, not sure why they named it button
  internal_chat = 'internal_chat',
  recording = 'recording',
  voicemail = 'voicemail',
  button_reply = 'button_reply',
  list = 'list',
  list_reply = 'list_reply',
}

export enum RChunkType {
  chatMessage = 'chat',
}

export enum ChunkState {
  PENDING = 'pending', // Clock
  SENT = 'sent', // Single tick
  DELIVERED = 'delivered', // Double tick
  READ = 'read', // Double tick with a blue color
  ERROR = 'error', // This state indicates retry can happen
  FAILED = 'failed', // This state indicates retry won't be possible
  UNDELIVERED = 'undelivered', // This state indicates the message was not delivered, retry can't happen
}

// Define reusable types for chunk metadata
interface ChatMessageMeta {
  sender: string
  message: string
  state: ChunkState
  stateReason?: string
}

interface FileMessageMeta {
  sender: string
  fileData: FileMessageContent['message']
  state: ChunkState
  stateReason?: string
  senderId: string
  message?: string
}

interface NotesMeta {
  message: string
}

interface InternalChatMeta {
  message: string
  channel_meta: RChunk['channel_meta'] // needed for global and specific error messages
  from_uuid: string
  from_name: string
  state: ChunkState
  stateReason?: string
}

interface SignalMeta {
  eventName: string
  message: string
  botUuid?: string
  transferType?: string
  attributes?: RSignalChunk['attributes']
}

interface DispositionMeta {
  description: string
  disposition: string
  subDisposition: string
}

interface TextMessageMeta {
  message: string
  state: ChunkState
  stateReason?: string
  channel_meta: RChunk['channel_meta'] // needed for global and specific error messages
  repliedMessage?: string
}

interface WhatsappButton {
  id: string
  title: string
}

interface WhatsappHeader {
  type: 'text' | 'media'
  text?: string
  media?: {
    media_type: string
    name: string
    size: number
    url: string
  }
}

interface WhatsappButtonMessage {
  body: string
  buttons: WhatsappButton[]
  footer?: string
  header?: WhatsappHeader
}

interface ButtonMeta {
  originalMessage: string // This is the original message that was replied to
  replyMessage: string // This is the message that was sent in response to the original message
  template?: RWhatsappTemplate
  templateData?: TemplateData
  message: WhatsappButtonMessage[]
}

interface ButtonReplyMeta {
  message: {
    id: string
    title: string
    description: string | null
  }
}

interface RRecordingDetails {
  RecordingDuration: string
  RecordingEndTime: string
  RecordingFormat: string
  RecordingResourceURL: string
  RecordingStartTime: string
  RecordingURL: string
  RecordingUUID: string
}

interface RecordingMeta {
  recordings: RRecordingDetails[]
  isInbound: boolean
}

// Add new interfaces for list and list_reply meta
interface ListRow {
  id: string
  title: string
  description?: string
}

interface ListSection {
  title: string
  rows: ListRow[]
}

interface ListMeta {
  header?: string
  body: string
  footer?: string
  button: string
  sections: ListSection[]
}

interface ListReplyMeta {
  id: string
  title: string
  description?: string
}

// CHUNK_STEP_5
// Update ChunkMeta to use the new types
export type ChunkMeta = {
  [ChunkType.chatMessage]: ChatMessageMeta
  [ChunkType.text]: TextMessageMeta
  [ChunkType.file]: FileMessageMeta
  [ChunkType.notes]: NotesMeta
  [ChunkType.task]: RTask
  [ChunkType.disposition]: DispositionMeta
  [ChunkType.signal]: SignalMeta
  [ChunkType.aiMessage]: RAiMessageChunk['content']
  [ChunkType.unknown]: Record<string, unknown>
  [ChunkType.chatButton]: RChatButtonChunk['content']
  [ChunkType.chatMultiProduct]: RChatMultiProductChunk['content']
  [ChunkType.multiProductReply]: RChatMultiProductReplyChunk['content']
  [ChunkType.chatMenuButton]: RChatMenuButtonChunk['content']
  [ChunkType.flowExecution]: RFlowExecutionChunkMeta
  [ChunkType.template]: TemplateMeta
  [ChunkType.button]: ButtonMeta
  [ChunkType.internal_chat]: InternalChatMeta
  [ChunkType.recording]: RecordingMeta
  [ChunkType.voicemail]: RecordingMeta
  [ChunkType.button_reply]: ButtonReplyMeta
  [ChunkType.list]: ListMeta
  [ChunkType.list_reply]: ListReplyMeta
} // Update Chunk interface to use ChunkType and ChunkMeta

export enum ChunkSender {
  UNKNOWN = 'unknown',
  AGENT = 'agent',
  CUSTOMER = 'customer',
  BOT = 'bot',
  SYSTEM = 'system',
}
// Main Chunk Type that drives the UI
export interface Chunk<T extends ChunkType = ChunkType> {
  id: string
  timetoken: number
  meta: ChunkMeta[T]
  conversationNumber?: number
  contactUuid?: string
  channel: Channels
  origin?: ChunkOrigin
  sender: ChunkSender
  from?: string
  to?: string
  type: T
  label?: string | React.ReactNode
}
interface TemplateData {
  components: Array<{
    parameters: Array<{
      text?: string
      type: string
      image?: {
        link?: string
      }
      document?: {
        link?: string
      }
      video?: {
        link: string
      }
    }>
    type: string
  }>
  language: {
    code: string
    policy: string
  }
  name: string
  namespace: string
  template_uuid: string
}

// Add this interface to the file
interface TemplateMeta {
  templateData: TemplateData
  state: ChunkState
  stateReason?: string
  channel_meta: RChunk['channel_meta'] // needed for global and specific error messages
  template?: RWhatsappTemplate | null
}

export type RConversationDetailInternal = {
  chunks: Chunk[]
  templates: Record<string, RWhatsappTemplate>
  conversation_details: {
    customer_name: string
    conversation_closed: boolean
    handoff_caller?: HandoffCaller | null
  }
}
